/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import { useCallback } from 'react';

import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';

import type {
	useSpaceManagementOperationsSpacePropertiesQuery as useSpaceManagementOperationsSpacePropertiesQuery$data,
	useSpaceManagementOperationsSpacePropertiesQueryVariables as useSpaceManagementOperationsSpacePropertiesQuery$variables,
} from './__types__/useSpaceManagementOperationsSpacePropertiesQuery';
import { useSpaceRoleMode } from './useSpaceRoleMode';

const useSpaceManagementOperationsSpacePropertiesQuery = gql`
	query useSpaceManagementOperationsSpacePropertiesQuery($spaceKey: String!) {
		space(key: $spaceKey) {
			id
			currentUser {
				isAdmin
			}
			operations {
				operation
				targetType
			}
		}
	}
`;
interface SpaceManagementOperations {
	canManageSomeUsers: boolean;
	canManageUsers: boolean;
	canManageNonlicensedUsers: boolean;
	canManageGuestUsers: boolean;
	canManagePublicLinks: boolean;
	canManageLookAndFeel: boolean;
	canManageContent: boolean;
	canAdminister: boolean;
	loading: boolean;
}

export const MANAGE_USERS = 'manage_users';
export const MANAGE_NONLICENSED_USERS = 'manage_nonlicensed_users';
export const MANAGE_GUEST_USERS = 'manage_guest_users';
export const MANAGE_PUBLIC_LINKS = 'manage_public_links';
export const ADMINISTER = 'administer';
export const MANAGE_LOOK_AND_FEEL = 'manage_look_and_feel';
export const MANAGE_CONTENT = 'manage_content';

/**
 * Hook to check if the user has the required operations to manage a space.
 */
export const useSpaceManagementOperations = ({
	spaceKey,
	skip,
}: {
	spaceKey: string;
	skip?: boolean;
}): SpaceManagementOperations => {
	const { isRbacEnabled, loading: isLoadingSpaceRoleMode } = useSpaceRoleMode();
	const defaultSkip = !spaceKey || !isRbacEnabled;

	const { data, loading } = useQuery<
		useSpaceManagementOperationsSpacePropertiesQuery$data,
		useSpaceManagementOperationsSpacePropertiesQuery$variables
	>(useSpaceManagementOperationsSpacePropertiesQuery, {
		variables: { spaceKey },
		skip: skip !== undefined ? skip : defaultSkip,
		onError: (error) => {
			if (isUnauthorizedError(error)) {
				markErrorAsHandled(error);
			} else {
				getMonitoringClient().submitError(error, {
					attribution: Attribution.PERMISSIONS_EXPERIENCE,
				});
			}
		},
	});

	const hasSpaceOperation = useCallback(
		(
			spaceOperation:
				| typeof MANAGE_USERS
				| typeof MANAGE_NONLICENSED_USERS
				| typeof MANAGE_GUEST_USERS
				| typeof MANAGE_PUBLIC_LINKS
				| typeof ADMINISTER
				| typeof MANAGE_LOOK_AND_FEEL
				| typeof MANAGE_CONTENT,
		) => {
			return (
				data?.space?.operations?.some(
					(operation) =>
						operation?.operation === spaceOperation && operation?.targetType === 'space',
				) ?? false
			);
		},
		[data],
	);

	return {
		canManageSomeUsers:
			hasSpaceOperation(MANAGE_USERS) ||
			hasSpaceOperation(MANAGE_NONLICENSED_USERS) ||
			hasSpaceOperation(MANAGE_GUEST_USERS),
		canManageUsers: hasSpaceOperation(MANAGE_USERS),
		canManageNonlicensedUsers: hasSpaceOperation(MANAGE_NONLICENSED_USERS),
		canManageGuestUsers: hasSpaceOperation(MANAGE_GUEST_USERS),
		canManagePublicLinks: hasSpaceOperation(MANAGE_PUBLIC_LINKS),
		canManageLookAndFeel: hasSpaceOperation(MANAGE_LOOK_AND_FEEL),
		canAdminister: hasSpaceOperation(ADMINISTER),
		canManageContent: hasSpaceOperation(MANAGE_CONTENT),
		loading: loading || isLoadingSpaceRoleMode,
	};
};
